import { Link, useLocation } from "react-router-dom";
import SiteNavbar from "@/components/feature/SiteNavbar";
import SiteFooter from "@/components/feature/SiteFooter";

const helpLinks = [
  { icon: "ri-service-line", title: "Our Services", desc: "Cloud, security, IBM Power, and more.", href: "/services", internal: true },
  { icon: "ri-building-2-line", title: "Industries We Serve", desc: "IT tailored to your sector.", href: "/#industries", internal: false },
  { icon: "ri-award-line", title: "Why Acu-Comp", desc: "20+ years of trusted support.", href: "/#why-us", internal: false },
  { icon: "ri-customer-service-2-line", title: "Contact Us", desc: "Talk to a real engineer today.", href: "/#contact", internal: false },
];

export default function NotFound() {
  const location = useLocation();

  return (
    <div className="min-h-screen flex flex-col bg-background-50 text-foreground-900">
      <SiteNavbar />

      {/* Hero */}
      <section className="relative flex items-center justify-center overflow-hidden min-h-[640px] pt-24 pb-20 bg-gradient-to-br from-primary-900 via-primary-800 to-primary-950">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
          <span className="text-[14rem] md:text-[24rem] font-black leading-none text-background-50/5">
            404
          </span>
        </div>

        <div className="relative z-10 w-full max-w-3xl mx-auto px-4 md:px-8 text-center">
          <span className="inline-flex items-center gap-2 bg-background-50/10 border border-background-50/20 text-background-50 px-4 py-1.5 rounded-full text-xs font-medium mb-6">
            <i className="ri-error-warning-line"></i> Error 404
          </span>

          <h1 className="text-4xl md:text-6xl font-bold text-background-50 leading-tight">
            This page took a wrong turn.
          </h1>

          <p className="mt-6 text-lg text-background-100/90">
            We couldn't find the page you were looking for. It may have been moved, renamed, or never
            existed in the first place. Let's get you back on track.
          </p>

          <p className="mt-4 text-sm text-background-200/80">
            Requested URL:{" "}
            <span className="font-mono text-accent-300 bg-primary-950/50 px-2 py-0.5 rounded">
              {location.pathname}
            </span>
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/"
              className="bg-accent-500 hover:bg-accent-600 text-foreground-950 font-semibold px-8 py-4 rounded-md whitespace-nowrap cursor-pointer transition inline-flex items-center gap-2"
            >
              <i className="ri-home-4-line"></i> Back to Home
            </Link>
            <Link
              to="/services"
              className="border border-background-50/40 text-background-50 hover:bg-background-50/10 font-semibold px-8 py-4 rounded-md whitespace-nowrap cursor-pointer transition"
            >
              Explore Services
            </Link>
          </div>

          <div className="mt-10 text-sm text-background-200/80">
            Need a hand right now? Call{" "}
            <a href="tel:18007800632" className="font-semibold text-accent-500 cursor-pointer whitespace-nowrap">
              1-800-780-0632
            </a>
          </div>
        </div>
      </section>

      {/* Where to next */}
      <section className="flex-1 py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-primary-600 font-semibold text-sm uppercase tracking-widest">Popular Destinations</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-3 text-foreground-950">
              Where would you like to go?
            </h2>
            <p className="text-foreground-600 mt-4">
              Here are the pages most people are looking for.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {helpLinks.map((link) =>
              link.internal ? (
                <Link
                  key={link.title}
                  to={link.href}
                  className="group bg-background-100 hover:bg-background-50 border border-background-200 hover:border-primary-300 rounded-lg p-6 transition cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition">
                    <i className={`${link.icon} text-2xl`}></i>
                  </div>
                  <h3 className="font-bold mt-5 text-foreground-950">{link.title}</h3>
                  <p className="text-sm text-foreground-600 mt-2">{link.desc}</p>
                  <span className="inline-flex items-center gap-1 mt-4 text-primary-600 font-semibold text-sm">
                    Go now <i className="ri-arrow-right-line"></i>
                  </span>
                </Link>
              ) : (
                <a
                  key={link.title}
                  href={link.href}
                  className="group bg-background-100 hover:bg-background-50 border border-background-200 hover:border-primary-300 rounded-lg p-6 transition cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition">
                    <i className={`${link.icon} text-2xl`}></i>
                  </div>
                  <h3 className="font-bold mt-5 text-foreground-950">{link.title}</h3>
                  <p className="text-sm text-foreground-600 mt-2">{link.desc}</p>
                  <span className="inline-flex items-center gap-1 mt-4 text-primary-600 font-semibold text-sm">
                    Go now <i className="ri-arrow-right-line"></i>
                  </span>
                </a>
              ),
            )}
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}